import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import mapboxgl, { MapboxOptions } from 'mapbox-gl';

import { AreaContextService } from '@core/services/area-context/area-context.service';
import { GeoArcEditSessionService } from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { GeoAreaEditSessionService } from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { SiteContextService } from '@core/services/site-context/site-context.service';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { MAPBOX_TOKEN } from '@core/tokens/mapbox.token';
import { AutoUnsubscribe } from '@core/utils/auto-unsubscribe';

import { ContextMenuService } from '@shared/services/context-menu/context-menu.service';
import { isEditableTarget } from '@shared/utils/editable-target.utils';

import { applyPerformanceConfig } from '@features/components/visualization/mapbox/config/performance.config';
import {
  AREA_BACKDROP_ICON,
  BackdropControl,
  BackdropControlState,
  SITE_BACKDROP_ICON
} from '@features/components/visualization/mapbox/controls/backdrop-control';
import { GroupingControl } from '@features/components/visualization/mapbox/controls/grouping';
import {
  GeoArcEditPanelComponent,
  canCommitPath
} from '@features/components/visualization/mapbox/geo-arc-edit-panel/geo-arc-edit-panel.component';
import { GeoAreaEditPanelComponent } from '@features/components/visualization/mapbox/geo-area-edit-panel/geo-area-edit-panel.component';
import { GeoAreaModificationActionsService } from '@features/components/visualization/mapbox/geo-area-modification-actions.service';
import { IconMapGalia } from '@features/components/visualization/mapbox/icon-map-galia';
import { MapArcEditor } from '@features/components/visualization/mapbox/map-elements/map-arc-editor';
import { MapAreaEditor } from '@features/components/visualization/mapbox/map-elements/map-area-editor';
import { MapAreas } from '@features/components/visualization/mapbox/map-elements/map-areas';
import { MapCoordinatesMenu } from '@features/components/visualization/mapbox/map-elements/map-coordinates-menu';
import { MapElement } from '@features/components/visualization/mapbox/map-elements/map-element';
import { MapMarkers } from '@features/components/visualization/mapbox/map-elements/map-markers';
import { MapRoutes } from '@features/components/visualization/mapbox/map-elements/map-routes';
import {
  MapSiteContext,
  mapShowsZones,
  shouldOfferAreaBackdrop,
  shouldOfferSiteBackdrop
} from '@features/components/visualization/mapbox/map-elements/map-site-context';
import { MapEvents } from '@features/components/visualization/mapbox/map-events';
import { MapboxTooltip } from '@features/components/visualization/mapbox/mapbox-tooltip';
import { GaliaArea } from '@features/components/visualization/mapbox/models/galia-area';
import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';
import { MapFilterService } from '@features/components/visualization/mapbox/services/filter.service';
import { MapFocusService } from '@features/components/visualization/mapbox/services/focus.service';
import { MapRegistryService } from '@features/components/visualization/mapbox/services/registry.service';

@Component({
  selector: 'app-mapbox',
  imports: [GeoArcEditPanelComponent, GeoAreaEditPanelComponent],
  templateUrl: './mapbox.component.html'
})
export class MapboxComponent extends AutoUnsubscribe implements AfterViewInit, OnDestroy {
  private readonly center: GeoLocation = { lat: 44.837789, lng: -0.57918 };
  private readonly mapboxAccessToken = inject(MAPBOX_TOKEN);
  private readonly mapFilterService = inject(MapFilterService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly mapLayerRegistry = inject(MapRegistryService);
  protected readonly editSession = inject(GeoAreaEditSessionService);
  protected readonly arcSession = inject(GeoArcEditSessionService);
  private readonly siteContext = inject(SiteContextService);
  private readonly areaContext = inject(AreaContextService);
  private readonly areaModifications = inject(GeoAreaModificationActionsService);
  private readonly zone = inject(NgZone);
  private readonly visualFocus = inject(VisualFocusService);
  private readonly mapFocusService = inject(MapFocusService);
  private readonly contextMenu = inject(ContextMenuService);
  private readonly notifications = inject(NotificationService);
  protected readonly currentLang = toSignal(this.translate.onLangChange);
  canFilterData = input<boolean>(false);
  map!: mapboxgl.Map;
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  @ViewChild('mapElement', { static: false }) mapElement!: ElementRef;
  markers = input<GaliaMarker[]>([]);
  routes = input<GaliaRoute[]>([]);
  areas = input<GaliaArea[]>([]);
  /** Whether clicking a drawn boundary here opens it for reshaping. */
  areaEditingEnabled = input<boolean>(false);
  areaSelected = output<string>();
  tooltip = inject(MapboxTooltip);
  private isMapboxLoaded = signal(false);
  private mapElements: MapElement[] = [];
  private mapRoutes?: MapRoutes;
  private mapAreas?: MapAreas;
  private areaEditor?: MapAreaEditor;
  private arcEditor?: MapArcEditor;
  private siteContextLayer?: MapSiteContext;
  private hasFramedActiveSession = false;
  private isSiteControlAttached = false;
  private isAreaControlAttached = false;
  private siteContextControl = new BackdropControl(
    SITE_BACKDROP_ICON,
    {
      show: 'map.controls.show_sites',
      hide: 'map.controls.hide_sites',
      loading: 'map.controls.loading_sites'
    },
    () => this.siteContext.toggle()
  );
  private areaContextControl = new BackdropControl(
    AREA_BACKDROP_ICON,
    {
      show: 'map.controls.show_zones',
      hide: 'map.controls.hide_zones',
      loading: 'map.controls.loading_zones'
    },
    () => this.areaContext.toggle()
  );
  private mapLanguage = new MapboxLanguage();
  private resizeObserver?: ResizeObserver;
  private pendingResizeTimeout?: NodeJS.Timeout;
  private windowResizeListener?: () => void;
  private mapMarkers?: MapMarkers;
  private groupingControl = new GroupingControl((mode) => {
    this.mapMarkers?.changeGroupingMode(mode, this.markers());
    this.mapFilterService.isGroupingEnabled = mode;
  }, this.mapFilterService.isCurrentlyFiltering);

  constructor() {
    super();

    effect(() => {
      if (this.isMapboxLoaded()) {
        this.mapElements.forEach((element) => {
          element.init();
        });
        untracked(() =>
          this.mapFocusService.attach(this.map, this.mapLayerRegistry.getRegistry())
        );
      }
    });

    // Must be created before the framing effects below: within a flush, effects run in
    // creation order, so the session a frame is attributed to has to be reset first.
    effect(() => {
      this.editSession.activeUid();
      this.arcSession.activeUid();
      this.hasFramedActiveSession = false;
    });

    effect(() => {
      if (this.routes() && this.isMapboxLoaded()) {
        MapEvents.getInstance().emit('update-routes', this.routes());
        this.centerOnContents();
      }
    });

    effect(() => {
      if (this.markers() && this.isMapboxLoaded()) {
        MapEvents.getInstance().emit('update-markers', this.markers());
        this.centerOnContents();
        this.mapFilterService.markers = this.markers();
      }
    });

    effect(() => {
      if (this.areas() && this.isMapboxLoaded()) {
        MapEvents.getInstance().emit('update-areas', this.areas());
        this.centerOnContents();
      }
    });

    effect(() => {
      this.editSession.activeUid();
      // Deleting the drawn feature clears geometry without changing activeUid, so the
      // effect must also react to that or the map is left with an empty, dead session.
      this.editSession.geometry();
      this.arcSession.activeUid();
      this.arcSession.path();
      this.arcSession.vias();
      if (this.isMapboxLoaded()) {
        untracked(() => {
          // MapAreas and MapRoutes withhold the shape under edit; the editors take
          // ownership of it, rendering and hit-testing it themselves.
          this.mapAreas?.syncSession();
          this.mapRoutes?.syncSession();
          this.areaEditor?.syncSession();
          this.arcEditor?.syncSession();
        });
      }
    });

    effect(() => {
      const showsZones = mapShowsZones(this.areas().length, this.editSession.isActive());
      const offerSites = shouldOfferSiteBackdrop(
        this.areas().length,
        this.editSession.isActive()
      );
      const offerAreas = shouldOfferAreaBackdrop(
        showsZones,
        this.markers().length > 0 || this.routes().length > 0
      );

      // Backdrop services are application-wide, so a map that has no use for one must
      // withhold its contents as well as its control — otherwise a backdrop switched on
      // over one entity would follow the user onto every other entity's map.
      const sites = offerSites ? this.siteContext.displayedItems() : [];
      const zones = offerAreas ? this.areaContext.displayedItems() : [];
      const siteState = {
        isShown: this.siteContext.isShown(),
        isLoading: this.siteContext.isLoading()
      };
      const areaState = {
        isShown: this.areaContext.isShown(),
        isLoading: this.areaContext.isLoading()
      };

      if (this.isMapboxLoaded()) {
        untracked(() => {
          this.isSiteControlAttached = this.syncBackdropControl(
            this.siteContextControl,
            this.isSiteControlAttached,
            offerSites,
            siteState
          );
          this.isAreaControlAttached = this.syncBackdropControl(
            this.areaContextControl,
            this.isAreaControlAttached,
            offerAreas,
            areaState
          );
          this.siteContextLayer?.sync(sites);
          this.mapAreas?.setBackdrop(zones);
        });
      }
    });

    // Listen for translation loading
    const subs = this.translate.onLangChange.subscribe(() => {
      this.currentLang();
    });
    this.subscriptionsManager.add(subs);
  }

  /** The panel offers Discard and Done as buttons; this is the keyboard shortcut for both. */
  @HostListener('document:keydown', ['$event'])
  protected onArcSessionKeydown(event: KeyboardEvent): void {
    // The map shares the page with the entity's form, so a document-wide listener would
    // otherwise commit the session on the Enter that submits a text field, and discard it
    // on the Escape that dismisses an autocomplete.
    if (!this.arcSession.isActive() || isEditableTarget(event.target)) {
      return;
    }
    if (event.key === 'Escape') {
      this.arcSession.cancel();
    }
    if (
      event.key === 'Enter' &&
      canCommitPath(
        this.arcSession.path(),
        this.arcSession.isComputing(),
        this.arcSession.endpoints()
      )
    ) {
      this.arcSession.commit();
    }
  }

  ngAfterViewInit() {
    this.initMap();
    IconMapGalia.loadIcons(this.map);
    this.tooltip.setMap(this.map);
    this.setupResizeObserver();
    this.loadMap();
    this.addMapEvents();
  }

  override ngOnDestroy(): void {
    try {
      // First, and before `map.remove()`: the root-provided focus service repaints the layers
      // it captured, so it needs a live map, and anything below here can throw and would
      // otherwise leave it holding a map that is about to be gone. Unconditional, because the
      // service outlives this component and must never be left attached to a dead map.
      this.mapFocusService.detach();

      // Cleanup map elements
      this.mapElements.forEach((element) => {
        try {
          element.destroy();
        } catch (error) {
          console.warn('Error destroying map element:', error);
        }
      });

      // Cleanup resize handling
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = undefined;
      }

      if (this.pendingResizeTimeout) {
        clearTimeout(this.pendingResizeTimeout);
        this.pendingResizeTimeout = undefined;
      }

      if (this.windowResizeListener) {
        window.removeEventListener('resize', this.windowResizeListener);
        this.windowResizeListener = undefined;
      }

      // Cleanup tooltip
      this.tooltip.cleanup();

      // Cleanup filter service
      if (this.canFilterData()) {
        this.mapFilterService.disableFilterService();
      }

      // Cleanup map instance
      if (this.map && !this.map._removed) {
        this.map.remove();
        this.map = undefined!;
      }
    } catch (error) {
      console.error('Error during component cleanup:', error);
    } finally {
      super.ngOnDestroy();
    }
  }

  private addMapEvents(): void {
    this.mapElements.forEach((element) => {
      element.registerEvents();
    });
  }

  /**
   * Frames the map on its contents, at most once per edit session. Every framing call
   * site fires on each store round-trip, and a dragged vertex round-trips through the
   * store, so an unguarded call flies the camera mid-drag. The session is framed on the
   * first emission that has something to frame — not necessarily the first emission,
   * since context sites load asynchronously and the map can mount into a live session.
   */
  private centerOnContents() {
    if (!this.isMapboxLoaded()) {
      this.map.setCenter(this.center);
      return;
    }

    // Untracked: framing must not make the caller effects depend on the session.
    const inEditSession = untracked(
      () => this.editSession.isActive() || this.arcSession.isActive()
    );
    if (inEditSession && this.hasFramedActiveSession) {
      return;
    }

    if (
      this.markers().length === 0 &&
      this.routes().length === 0 &&
      this.areas().length === 0
    ) {
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();

    const totalItems =
      this.markers().length +
      this.routes().reduce((sum, route) => sum + route.coords.length, 0) +
      this.areas().reduce((sum, area) => sum + area.ring.length, 0);

    if (totalItems > 1000) {
      const sampleSize = Math.min(100, totalItems);
      const markerSample = this.markers().filter(
        (_, index) => index % Math.ceil(this.markers().length / sampleSize) === 0
      );

      markerSample.forEach((marker) => {
        bounds.extend(marker.coords);
      });

      this.routes().forEach((route) => {
        const coordSample = route.coords.filter(
          (_, index) => index % Math.ceil(route.coords.length / 10) === 0
        );
        coordSample.forEach((coord) => {
          bounds.extend(coord);
        });
      });

      this.areas().forEach((area) => {
        area.ring.forEach((point) => {
          bounds.extend(point);
        });
      });
    } else {
      this.markers().forEach((marker) => {
        bounds.extend(marker.coords);
      });

      this.routes().forEach((route) => {
        route.coords.forEach((coord) => {
          bounds.extend(coord);
        });
      });

      this.areas().forEach((area) => {
        area.ring.forEach((point) => {
          bounds.extend(point);
        });
      });
    }

    this.hasFramedActiveSession = inEditSession;

    this.map.fitBounds(bounds, {
      padding: 100,
      maxZoom: 15,
      duration: 1000
    });
  }

  private createMapElements(): void {
    const registry = this.mapLayerRegistry.getRegistry();

    this.mapRoutes = new MapRoutes(
      this.map,
      this.store,
      this.mapLayerRegistry,
      this.arcSession,
      this.zone,
      this.visualFocus
    );
    this.mapElements.push(this.mapRoutes);

    this.mapMarkers = new MapMarkers(
      this.map,
      this.tooltip,
      this.store,
      this.mapLayerRegistry,
      this.zone,
      this.visualFocus,
      this.mapFocusService
    );
    this.mapElements.push(this.mapMarkers);

    this.mapAreas = new MapAreas(this.map, this.mapLayerRegistry, this.editSession, {
      isEditable: (uid) =>
        this.areaEditingEnabled() &&
        !this.editSession.isActive() &&
        this.areaModifications.getModification(uid) !== undefined,
      onSelect: (uid) => this.areaSelected.emit(uid)
    });
    this.mapElements.push(this.mapAreas);

    this.areaEditor = new MapAreaEditor(this.map, this.editSession);
    this.mapElements.push(this.areaEditor);

    this.arcEditor = new MapArcEditor(this.map, this.arcSession);
    this.mapElements.push(this.arcEditor);

    this.siteContextLayer = new MapSiteContext(this.map, this.tooltip, this.translate);
    this.mapElements.push(this.siteContextLayer);

    this.mapElements.push(
      new MapCoordinatesMenu(this.map, this.contextMenu, this.translate, this.notifications)
    );

    this.mapFilterService.changeGroupingFonction = this.mapMarkers.changeGroupingMode.bind(
      this.mapMarkers
    );
    this.mapFilterService.setRegistry(registry);
  }

  private initMap(): void {
    mapboxgl.accessToken = this.mapboxAccessToken;

    const baseMapOptions: MapboxOptions = {
      container: this.mapElement.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v12?optimize=true',
      zoom: 5,
      center: this.center
    };

    const optimizedMapOptions = applyPerformanceConfig(baseMapOptions);
    this.map = new mapboxgl.Map(optimizedMapOptions);

    this.mapLayerRegistry.init({
      markerLayerName: 'markers-layer',
      markerSourceName: 'markers-source',
      routeLayerName: 'routes-layer',
      routeSourceName: 'routes-source',
      areaLayerName: 'areas-layer',
      areaSourceName: 'areas-source'
    });

    this.map.dragRotate.disable();
    this.map.touchPitch.disable();

    this.addMapControls();
    this.createMapElements();

    // Activate the filter service if filtering is enabled
    if (this.canFilterData()) {
      this.mapFilterService.activateFilterService(this.map);
    }
  }

  /**
   * Adds or removes a backdrop control as the map's contents make it relevant, and returns
   * whether it is now attached.
   */
  private syncBackdropControl(
    control: BackdropControl,
    isAttached: boolean,
    offer: boolean,
    state: BackdropControlState
  ): boolean {
    if (offer && !isAttached) {
      this.map.addControl(control);
    } else if (!offer && isAttached) {
      this.map.removeControl(control);
      return false;
    }

    control.update(state);
    return offer;
  }

  private addMapControls(): void {
    const controls = [
      new mapboxgl.NavigationControl({ showCompass: false }),
      new mapboxgl.FullscreenControl(),
      this.groupingControl,
      this.mapLanguage
    ];

    controls.forEach((control) => this.map.addControl(control));
  }

  private loadMap() {
    this.map.on('load', () => {
      this.map.resize();
      this.centerOnContents();
      this.isMapboxLoaded.set(true);
    });
  }

  private setupResizeObserver() {
    const RESIZE_DEBOUNCE_MS = 250;

    const handleResize = () => {
      if (this.pendingResizeTimeout) {
        clearTimeout(this.pendingResizeTimeout);
      }

      this.pendingResizeTimeout = setTimeout(() => {
        this.pendingResizeTimeout = undefined;
        if (this.map && !this.map._removed) {
          requestAnimationFrame(() => {
            this.map.resize();
          });
        }
      }, RESIZE_DEBOUNCE_MS);
    };

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(handleResize);
      this.resizeObserver.observe(this.mapContainer.nativeElement);
    } else {
      this.windowResizeListener = handleResize;
      window.addEventListener('resize', this.windowResizeListener, { passive: true });
    }
  }
}
